<template>
  <v-card class="pa-4" elevation="1">
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="6">
        <h2 class="text-h6 font-weight-bold primary--text">
          <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
          Contratación Masiva de Candidatos
        </h2>
        <div class="caption text--secondary">
          Seleccione un Requerimiento para cargar a los candidatos evaluados e ingrese los parámetros del lote.
        </div>
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-btn
          color="primary"
          :disabled="!candidates.length || isSaving"
          :loading="isSaving"
          @click="generateMassiveContracts"
        >
          <v-icon left>mdi-file-document-multiple-outline</v-icon>
          Generar {{ selectedCandidates.length }} Contratos Masivos
        </v-btn>
      </v-col>
    </v-row>

    <v-divider class="my-3"></v-divider>

    <!-- Panel de Selección de Requerimiento y Parámetros Globales -->
    <v-row dense>
      <v-col cols="12" md="4">
        <v-autocomplete
          v-model="selectedReqID"
          :items="requirements"
          item-text="ReqName"
          item-value="ReqID"
          label="Seleccionar Requerimiento *"
          dense
          outlined
          clearable
          @change="onRequirementChange"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ item.ReqName || ('REQ-' + item.ReqID) }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                Puesto: {{ item.PstName }} | Vacantes libres: {{ item.FreeVacancies || 0 }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="6" md="2">
        <v-menu
          v-model="menuBeginDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="globalParams.AgtBeginDate"
              label="Fecha Inicio *"
              readonly
              dense
              outlined
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="globalParams.AgtBeginDate"
            no-title
            @input="menuBeginDate = false; applyGlobalParamsToCandidates()"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="6" md="2">
        <v-menu
          v-model="menuEndDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="globalParams.AgtEndDate"
              label="Fecha Fin *"
              readonly
              dense
              outlined
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="globalParams.AgtEndDate"
            no-title
            @input="menuEndDate = false; applyGlobalParamsToCandidates()"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" md="2">
        <v-text-field
          v-model="globalParams.Cuadrilla"
          label="Cuadrilla"
          dense
          outlined
          placeholder="Ej. Cuadrilla 01"
          @change="applyGlobalParamsToCandidates"
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="2">
        <s-select-definition
          v-model="globalParams.TypeWorker"
          :def-id="1039"
          label="Tipo Trabajador"
          dense
          outlined
          @change="applyGlobalParamsToCandidates"
        />
      </v-col>
    </v-row>

    <!-- Resumen y Filtro de Candidatos -->
    <v-row dense align="center" class="mt-1">
      <v-col cols="12" md="6">
        <v-chip class="mr-2" color="blue lighten-5" text-color="blue darken-2">
          Total Candidatos: {{ candidates.length }}
        </v-chip>
        <v-chip color="green lighten-5" text-color="green darken-2">
          Seleccionados: {{ selectedCandidates.length }}
        </v-chip>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchFilter"
          append-icon="mdi-magnify"
          label="Buscar candidato por DNI o Nombre..."
          single-line
          hide-details
          dense
          outlined
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Tabla Interactiva de Candidatos -->
    <v-data-table
      v-model="selectedCandidates"
      :headers="headers"
      :items="candidates"
      :search="searchFilter"
      :loading="isLoading"
      show-select
      item-key="CprID"
      class="elevation-0 border mt-3"
      no-data-text="No hay candidatos pendientes de contratación para el requerimiento seleccionado."
    >
      <!-- Foto / Avatar -->
      <template v-slot:item.NtpFullName="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar size="36" class="mr-3 grey lighten-2">
            <v-icon color="grey darken-1">mdi-account</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-medium">{{ item.NtpFullName }}</div>
            <div class="caption text--secondary">DNI: {{ item.PrsDocumentNumber }}</div>
          </div>
        </div>
      </template>

      <!-- Condición del Trabajador -->
      <template v-slot:item.IsExistingWorker="{ item }">
        <v-chip
          small
          :color="item.IsExistingWorker ? 'purple lighten-5' : 'teal lighten-5'"
          :text-color="item.IsExistingWorker ? 'purple darken-2' : 'teal darken-2'"
        >
          {{ item.IsExistingWorker ? 'Reingreso' : 'Nuevo' }}
        </v-chip>
      </template>

      <!-- Sueldo Editable -->
      <template v-slot:item.AgtSalary="{ item }">
        <v-text-field
          v-model="item.AgtSalary"
          dense
          hide-details
          outlined
          prefix="S/"
          style="max-width: 110px"
        ></v-text-field>
      </template>

      <!-- Cuadrilla por Fila -->
      <template v-slot:item.Cuadrilla="{ item }">
        <v-text-field
          v-model="item.Cuadrilla"
          dense
          hide-details
          outlined
          style="max-width: 140px"
        ></v-text-field>
      </template>

      <!-- Badges de Estado Previsional / Banco -->
      <template v-slot:item.Badges="{ item }">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                x-small
                class="mr-1"
                :color="item.BatID ? 'green lighten-4' : 'grey lighten-3'"
                :text-color="item.BatID ? 'green darken-3' : 'grey darken-1'"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left x-small>mdi-bank</v-icon>
                {{ item.BatID ? 'Banco' : 'Sin Banco' }}
              </v-chip>
            </template>
            <span>{{ item.BatID ? 'Cuenta Bancaria Registrada' : 'Cuenta Pendiente (Se agregará post-firma)' }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                x-small
                :color="item.PsnID ? 'blue lighten-4' : 'grey lighten-3'"
                :text-color="item.PsnID ? 'blue darken-3' : 'grey darken-1'"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left x-small>mdi-shield-account</v-icon>
                {{ item.PsnID ? 'AFP' : 'Sin AFP' }}
              </v-chip>
            </template>
            <span>{{ item.PsnID ? 'Régimen Pensionario Registrado' : 'AFP/ONP Pendiente' }}</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import _sCttoService from "@/services/HumanResource/HmnContractService";
import sSelectDefinition from "@/components/Utils/SSelectDefinition";

export default {
  name: "ContractFormMassive",
  components: {
    sSelectDefinition,
  },
  data() {
    return {
      selectedReqID: null,
      requirements: [],
      candidates: [],
      selectedCandidates: [],
      isLoading: false,
      isSaving: false,
      searchFilter: "",
      menuBeginDate: false,
      menuEndDate: false,
      globalParams: {
        AgtBeginDate: new Date().toISOString().substr(0, 10),
        AgtEndDate: new Date(new Date().setMonth(new Date().getMonth() + 3))
          .toISOString()
          .substr(0, 10),
        Cuadrilla: "",
        TypeWorker: 1,
      },
      headers: [
        { text: "Candidato", value: "NtpFullName", sortable: true },
        { text: "Condición", value: "IsExistingWorker", sortable: true },
        { text: "Cargo", value: "PstName", sortable: true },
        { text: "Sueldo (S/)", value: "AgtSalary", sortable: false, width: "130px" },
        { text: "Cuadrilla", value: "Cuadrilla", sortable: false, width: "160px" },
        { text: "Datos Previsionales / Banco", value: "Badges", sortable: false },
      ],
    };
  },
  mounted() {
    this.loadRequirements();
  },
  methods: {
    async loadRequirements() {
      try {
        const res = await _sCttoService.listContracts({}, this.$fun.getUserID());
        this.requirements = (res.data || []).map((r) => ({
          ReqID: r.ReqID || r.AgtID,
          ReqName: r.AreName ? `${r.AreName} - ${r.PstName}` : `REQ-${r.ReqID}`,
          PstName: r.PstName || "Cosechador",
          FreeVacancies: 50,
          ...r,
        }));
      } catch (e) {
        console.warn("Error cargando requerimientos:", e);
      }
    },
    async onRequirementChange() {
      if (!this.selectedReqID) {
        this.candidates = [];
        this.selectedCandidates = [];
        return;
      }

      this.isLoading = true;
      try {
        const res = await _sCttoService.candidatesByRequirement(
          this.selectedReqID,
          this.$fun.getUserID()
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          this.candidates = res.data.map((c) => ({
            ...c,
            AgtSalary: c.AgtSalary || "1025.00",
            Cuadrilla: this.globalParams.Cuadrilla || "Cuadrilla 01",
            AgtBeginDate: this.globalParams.AgtBeginDate,
            AgtEndDate: this.globalParams.AgtEndDate,
          }));
          this.selectedCandidates = [...this.candidates];
        }
      } catch (e) {
        console.error("Error al cargar candidatos:", e);
        this.$fun.alert("Error al obtener los candidatos del requerimiento", "error");
      } finally {
        this.isLoading = false;
      }
    },
    applyGlobalParamsToCandidates() {
      this.candidates.forEach((c) => {
        if (this.globalParams.Cuadrilla) c.Cuadrilla = this.globalParams.Cuadrilla;
        c.AgtBeginDate = this.globalParams.AgtBeginDate;
        c.AgtEndDate = this.globalParams.AgtEndDate;
      });
    },
    async generateMassiveContracts() {
      if (!this.selectedCandidates.length) {
        this.$fun.alert("Seleccione al menos un candidato para generar contratos.", "warning");
        return;
      }

      const confirm = await this.$fun.alert(
        `¿Confirmar la creación masiva de ${this.selectedCandidates.length} contratos en estado BORRADOR?`,
        "question"
      );

      if (!confirm.value) return;

      this.isSaving = true;
      try {
        const payload = this.selectedCandidates.map((c) => ({
          CprID: c.CprID,
          NtpID: c.NtpID,
          ReqID: c.ReqID || this.selectedReqID,
          AgtBeginDate: c.AgtBeginDate || this.globalParams.AgtBeginDate,
          AgtEndDate: c.AgtEndDate || this.globalParams.AgtEndDate,
          AgtSalary: c.AgtSalary,
          TypeWorker: this.globalParams.TypeWorker,
          TypePayroll: c.TypePayroll || 1,
          Cuadrilla: c.Cuadrilla || this.globalParams.Cuadrilla,
          PstID: c.PstID,
          AreID: c.AreID,
          CceID: c.CceID,
          CmpID: c.CmpID,
          TypeContract: c.TypeContract || 1,
        }));

        const res = await _sCttoService.saveMassive(payload, this.$fun.getUserID());
        if (res.status === 200) {
          this.$fun.alert(
            `¡Éxito! Se crearon ${res.data.successCount || payload.length} contratos en borrador listos para firma.`,
            "success"
          );
          this.onRequirementChange(); // Reload
        }
      } catch (e) {
        console.error("Error al generar contratos masivos:", e);
        this.$fun.alert("Ocurrió un error al procesar la inserción masiva.", "error");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.border {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
